<template>
  <div class="row q-px-md">
    <CLoading :loading="loading" />
    <DateRangeSelectorV2
      :disable="false"
      v-model="dateRange"
      :opens="'right'"
      @update:onChangeDateRange="changeDateRange"
    ></DateRangeSelectorV2>
    <q-space />
  </div>
  <div class="q-pa-md row q-gutter-md text-center">
    <div class="col">
      <OverviewCard
        :title-main="$t('label.push_messages')"
        :title-sub="$t('label.remaining')"
        :main-value="numberWithCommas(remainingMessages)"
        main-value-unit="通"
        main-value-class="text-positive"
        :is-minus-value="false"
        :show-main-value-sign="false"
        :additional-info="remainingInfo"
        :show-action-button="false"
        :show-progress-bar="true"
        :progress-value="remainingProgressValue"
        progress-color="positive"
      />
    </div>
    <div class="col">
      <OverviewCard
        :title-main="$t('label.active_users_title_main')"
        :title-sub="$t('label.active_users_title_sub')"
        :main-value="numberWithCommas(diffActiveUsers)"
        main-value-unit="人"
        :main-value-class="isMinusActiveUsers ? 'text-negative' : 'text-positive'"
        :is-minus-value="isMinusActiveUsers"
        :show-main-value-sign="true"
        :additional-info="activeUsersInfo"
        :show-action-button="true"
        :action-button-label="$t('details')"
        @action-click="onDetails"
      />
    </div>
    <div class="col">
      <OverviewCard
        :title-main="$t('label.block_rate_title_main')"
        :title-sub="$t('label.block_rate_title_sub')"
        :main-value="diffBlockedUsers"
        main-value-unit="%"
        :main-value-class="isMinusBlockedUsers ? 'text-negative' : ''"
        :is-minus-value="isMinusBlockedUsers"
        :show-main-value-sign="false"
        :additional-info="blockedUsersInfo"
        :show-action-button="false"
      />
    </div>
    <div class="col">
      <OverviewCard
        :title-main="$t('label.popup_click_title_main')"
        :title-sub="$t('label.popup_click_title_sub')"
        :main-value="numberWithCommas(totalPopupClick)"
        :is-minus-value="false"
        :show-main-value-sign="false"
        :additional-info="popupClickInfo"
      />
    </div>
    <div class="col">
      <OverviewCard
        :title-main="$t('label.story_deliveries_title_main')"
        :title-sub="$t('label.story_deliveries_title_sub')"
        :main-value="storyDeliveries"
        :is-minus-value="false"
        :show-main-value-sign="false"
        :additional-info="storyDeliveriesInfo"
      />
    </div>
    <div class="col">
      <OverviewCard
        :title-main="$t('label.form_responses_title_main')"
        :title-sub="$t('label.form_responses_title_sub')"
        :main-value="formResponses"
        :is-minus-value="false"
        :show-main-value-sign="false"
        :additional-info="formResponsesInfo"
      />
    </div>
  </div>
  <div class="q-pa-md">
    <div class="row q-gutter-sm q-mb-sm">
      <div class="col">
        <UserTable
          :title="$t('label.user_metrics')"
          :description="$t('label.user_metrics_description')"
          :app-id="appId"
          :date-range="dateRange"
        />
      </div>
      <div class="col">
        <PopupTable
          :title="$t('label.popup_messages')"
          :description="$t('label.popup_messages_description')"
          :app-id="appId"
          :date-range="dateRange"
        />
      </div>
    </div>
    <div class="row q-gutter-sm q-mb-sm">
      <div class="col">
        <RichMenuTable
          :title="$t('label.rich_menu_messages')"
          :description="$t('label.rich_menu_messages_description')"
          :app-id="appId"
          :date-range="dateRange"
        />
      </div>
      <div class="col">
        <StoryTable
          :title="$t('label.story_messages')"
          :description="$t('label.story_messages_description')"
          :app-id="appId"
          :date-range="dateRange"
        />
      </div>
    </div>
    <div class="row q-gutter-sm q-mb-sm">
      <div class="col">
        <CommonMessagesTable
          :title="$t('label.initial_messages')"
          :description="$t('label.initial_messages_description')"
          :app-id="appId"
          :date-range="dateRange"
          :message-type="MESSAGE_TYPE.INITIAL"
        />
      </div>
      <div class="col">
        <CommonMessagesTable
          :title="$t('label.push_messages')"
          :description="$t('label.push_messages_description')"
          :app-id="appId"
          :date-range="dateRange"
          :message-type="MESSAGE_TYPE.PUSH"
        />
      </div>
    </div>
    <div class="row q-gutter-sm q-mb-sm">
      <div class="col">
        <FormTable
          :title="$t('label.form_metrics')"
          :description="$t('label.form_metrics_description')"
          :app-id="appId"
          :date-range="dateRange"
        />
      </div>
      <div class="col"></div>
    </div>
  </div>
</template>

<script lang="ts">
import { mixins, Options } from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { maska } from 'maska'
import { IDateRangePicker } from '@/utils/types'
import { ACTION_APP } from '@/store/actions'
import AppMixin from '@/components/app/mixins/AppMixin.vue'
import OverviewCardMixin from './mixins/OverviewCardMixin.vue'
import CLoading from '@/components/common/ui/CLoading.vue'
import BaseFormMixin from '../common/mixins/BaseFormMixin.vue'
import DateRangeSelectorV2 from '@/components/common/ui/DateRangeSelectorV2.vue'
import { DatetimeUtils } from '@/utils/datetime'
import { UserTable, PopupTable, RichMenuTable, CommonMessagesTable, StoryTable, FormTable } from './tables'
import OverviewCard from './OverviewCard.vue'
import { MESSAGE_TYPE } from '@/utils/constants'

@Options({
  components: {
    DateRangeSelectorV2,
    CLoading,
    UserTable,
    PopupTable,
    RichMenuTable,
    CommonMessagesTable,
    OverviewCard,
    StoryTable,
    FormTable,
  },
  directives: { maska },
  async beforeRouteLeave() {
    if (!this) {
      return
    }
    const result = await this.checkDraftValue()
    if (result) {
      return true
    } else {
      return false
    }
  },
})
export default class OverView extends mixins(AppMixin, BaseFormMixin, OverviewCardMixin) {
  loading = true

  dateRange: IDateRangePicker = {
    startDate: '',
    endDate: '',
  }

  MESSAGE_TYPE = MESSAGE_TYPE

  firstLoad = true

  async fetchSelectingApp() {
    this.resetCardData()

    if (!this.appId) {
      return
    }

    this.loading = true

    const resp = await this.$store.dispatch(ACTION_APP.OVERVIEW, {
      app_id: this.appId,
      is_test_mode: false,
      date_from: DatetimeUtils.formatDate(this.dateRange.startDate),
      date_to: DatetimeUtils.formatDate(this.dateRange.endDate),
    })
    if (resp) {
      this.updateCardData(resp)
    }

    this.loading = false
  }

  async changeDateRange(value) {
    if (this.firstLoad) {
      return
    }
    this.dateRange = value
    await this.fetchSelectingApp()
  }

  @Watch('appId', { immediate: true })
  async appIdChanged() {
    this.dateRange = DatetimeUtils.getDateRange('last7Days')
    await this.fetchSelectingApp()
    this.firstLoad = false
  }

  onDetails() {
    this.goto('overview_details', {
      app_id: this.appId,
      from_date: DatetimeUtils.formatDate(this.dateRange.startDate),
      to_date: DatetimeUtils.formatDate(this.dateRange.endDate),
    })
  }
}
</script>

<style lang="scss" scoped>
.my-card {
  width: 100%;
  min-height: 221px;
}
</style>
